/**
 * Оболочка приложения: верхняя панель и боковой рейл.
 *
 * Навигация всегда слева. Ниже 900px подписи рейла скрываются,
 * разделы остаются на виду, включая «Приёмники» и «Дамп базы».
 *
 * В шапке выбирается база 1С. Смена базы пересоздаёт открытый раздел:
 * каталог и дамп другой базы не должны показывать данные прежней.
 */

import {
  AppstoreOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  DownloadOutlined,
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SettingOutlined,
  SunOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { Select } from "antd";
import type { ComponentType } from "react";
import { useState } from "react";
import { NavLink, Outlet, useMatches, useNavigate } from "react-router-dom";

import { notify } from "../api/notify";
import type { RouteHandle } from "../routes";
import { useAuth } from "../stores/auth";
import { useSources } from "../stores/sources";
import { useTheme } from "../stores/theme";

interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType;
}

const NAV: NavItem[] = [
  { to: "/", label: "Обзор", Icon: AppstoreOutlined },
  { to: "/tables", label: "Таблицы", Icon: UnorderedListOutlined },
  { to: "/jobs", label: "Задания", Icon: ClockCircleOutlined },
  { to: "/runs", label: "Запуски", Icon: BarChartOutlined },
  { to: "/sources", label: "Базы 1С", Icon: DatabaseOutlined },
  { to: "/targets", label: "Приёмники", Icon: SettingOutlined },
  { to: "/dump", label: "Дамп базы", Icon: DownloadOutlined },
];

const RAIL_KEY = "sl1ce.rail";

export function AppLayout() {
  const navigate = useNavigate();
  const matches = useMatches();
  const user = useAuth((state) => state.user);
  const signOut = useAuth((state) => state.signOut);
  const sources = useSources((state) => state.sources);
  const currentId = useSources((state) => state.currentId);
  const loaded = useSources((state) => state.loaded);
  const selectSource = useSources((state) => state.select);
  const dark = useTheme((state) => state.dark);
  const toggleTheme = useTheme((state) => state.toggle);
  const [leaving, setLeaving] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(RAIL_KEY) === "collapsed",
  );

  const handle = (matches.at(-1)?.handle ?? {}) as RouteHandle;

  function toggleRail(): void {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(RAIL_KEY, next ? "collapsed" : "open");
  }

  async function leave(): Promise<void> {
    setLeaving(true);
    try {
      await signOut();
      notify.info("Сеанс завершён");
      void navigate("/login", { replace: true });
    } finally {
      setLeaving(false);
    }
  }

  const FoldIcon = collapsed ? MenuUnfoldOutlined : MenuFoldOutlined;

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <NavLink
          className="app-brand"
          to="/"
          title="sl1ce — синхронизация 1С в PostgreSQL"
        >
          <span className="app-brand-name">
            sl<span className="app-brand-one">1</span>ce
          </span>
          <span className="app-brand-tag">1С&nbsp;→&nbsp;PostgreSQL</span>
        </NavLink>

        {handle.back ? (
          <button
            className="app-chrome-icon"
            type="button"
            aria-label="Назад"
            title="Назад"
            onClick={() => {
              void navigate(-1);
            }}
          >
            <LeftOutlined />
          </button>
        ) : null}

        {handle.back && handle.title ? (
          <span className="app-topbar-title">{handle.title}</span>
        ) : null}

        <div className="app-topbar-meta">
          <div className="app-meta">
            <span className="app-meta-label">База 1С</span>
            {sources.length ? (
              <Select
                size="small"
                variant="borderless"
                value={currentId ?? undefined}
                popupMatchSelectWidth={false}
                style={{ minWidth: 160 }}
                options={sources.map((source) => ({
                  value: source.id,
                  label: source.rejected
                    ? `${source.name} — вход не проходит`
                    : source.name,
                }))}
                onChange={(id: string) => selectSource(id)}
              />
            ) : (
              <NavLink className="app-meta-value" to="/sources">
                {loaded ? "добавьте базу" : "…"}
              </NavLink>
            )}
          </div>
          <span className="app-meta-sep" aria-hidden="true" />
          <div className="app-meta">
            <span className="app-meta-label">Пользователь</span>
            <span className="app-meta-value">{user?.login ?? "—"}</span>
          </div>
        </div>

        <div className="app-topbar-actions">
          <button
            className="app-chrome-icon"
            type="button"
            title={dark ? "Светлая тема" : "Тёмная тема"}
            aria-label={dark ? "Включить светлую тему" : "Включить тёмную тему"}
            onClick={toggleTheme}
          >
            {dark ? <SunOutlined /> : <MoonOutlined />}
          </button>
          <button
            className="app-chrome-btn"
            type="button"
            disabled={leaving}
            onClick={() => {
              void leave();
            }}
          >
            Выйти
          </button>
        </div>
      </header>

      <div className="app-body">
        <nav
          className={collapsed ? "app-rail is-collapsed" : "app-rail"}
          aria-label="Разделы"
        >
          <ul className="app-rail-nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  className={({ isActive }) =>
                    isActive ? "app-rail-item is-active" : "app-rail-item"
                  }
                  end={item.to === "/"}
                  to={item.to}
                  title={item.label}
                >
                  <item.Icon />
                  <span className="app-rail-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          <button
            className="app-rail-toggle"
            type="button"
            title={collapsed ? "Развернуть меню" : "Свернуть меню"}
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
            onClick={toggleRail}
          >
            <FoldIcon />
            <span className="app-rail-label">Свернуть</span>
          </button>
        </nav>

        <main className="app-content">
          <Outlet key={currentId ?? "none"} />
        </main>
      </div>
    </div>
  );
}
